import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { invalidateAfterMutation } from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import {
  outboxEndpoints,
  type OutboxService,
} from "@/features/outbox/api/endpoints";
import type { ReplayOutboxEventRequest } from "@/features/outbox/api/dto";

interface ReplayArgs {
  service: OutboxService;
  outboxEventId: string;
  reason: string;
}

/**
 * Rejoue un événement en `DEAD_LETTER`
 * (`POST /gw/<service>/api/v1/OutboxEvents/{id}/replay`, guide v2.5).
 *
 * Remise en `PENDING` (fenêtre de retries remise à zéro). Refusé (422) si
 * l'événement n'est pas en `DEAD_LETTER`.
 * Rôles : `flashpay-superadmin` / `flashpay-admin` (wallet/admin/ledger/
 * merchant) — `aucun rôle particulier` (kyc/fee-engine, fallback gateway).
 */
export const useReplayOutboxEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["outbox", "events", "replay"],
    mutationFn: ({ service, outboxEventId, reason }: ReplayArgs) => {
      const body: ReplayOutboxEventRequest = { reason };
      return mutateData({
        url: outboxEndpoints.replay(service, outboxEventId),
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      });
    },
    onSuccess: (_data, variables) =>
      invalidateAfterMutation(queryClient, [
        ["OUTBOX_EVENTS_LIST", variables.service],
      ]),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useReplayOutboxEvent failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "outbox" }
      );
    },
  });
};
